import './index.css'
import '../../styles/card-group.css'
import { useEffect, useMemo, useState } from 'react'
import { Plus, Pencil, Trash2, FastForward, Circle, CheckCircle2, CreditCard, Search, X, ChevronDown, ChevronUp } from 'lucide-react'
import { fmtAmt } from '../../lib/format'
import { toDisplayDate } from '../../lib/dateUtils'
import type { Category, Transaction, TransactionListProps } from '../../types'
import { buildRecentTransactionItems } from '../RecentTransactionsList/grouping'
import { groupInstallments } from '../../lib/installmentGrouping'
import { filterTransactionsBySearch } from './search'

const CAT_ABBR: Record<string, string> = {
  'Salário': 'SA', 'Mercado': 'ME', 'Aluguel': 'AL', 'Cartão de crédito': 'CC',
  'Terapia': 'TE', 'Lazer': 'LA', 'Transporte': 'TR', 'Saúde': 'SD',
  'Assinatura': 'AS', 'Esportes': 'ES', 'FIES': 'FI', 'Celular': 'CE',
  'Empréstimo': 'EM', 'Seguro': 'SG', 'Beleza': 'BL', 'Viagem': 'VI',
}

const SKELETONS = [1, 2, 3, 4]

export default function TransactionList({
  items,
  cards = [],
  subcategories,
  onAdd,
  loading,
  onDelete,
  onEdit,
  onAmortize,
  onDeleteGroup,
  onToggleRealized,
  onViewInvoice,
  title = 'Lançamentos recentes',
}: TransactionListProps & { title?: string }) {
  const [query, setQuery] = useState('')
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const filteredItems = useMemo(() => filterTransactionsBySearch(items, query, cards), [cards, items, query])
  const groupedItems = useMemo(
    () => (onViewInvoice ? buildRecentTransactionItems(filteredItems, cards) : null),
    [cards, filteredItems, onViewInvoice],
  )

  useEffect(() => {
    setQuery('')
  }, [items])

  function subName(id: string | null): string | undefined {
    if (!id) return undefined
    return (subcategories as Category[]).find(s => s.id === id)?.name
  }

  function toggleGroup(id: string) {
    setExpandedGroups(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function renderTransaction(t: Transaction, isChild = false) {
    return (
      <div key={`tx-${t.id}`} className={'bc-tx' + (t.realized ? '' : ' bc-tx--unrealized')} style={isChild ? { paddingLeft: 44, background: 'var(--sand-1)' } : undefined}>
        {onToggleRealized && (
          <button
            className="bc-tx__realize"
            aria-label="Marcar como efetivado"
            onClick={() => onToggleRealized(t.id, t.realized)}
          >
            {t.realized ? <CheckCircle2 size={16} /> : <Circle size={16} />}
          </button>
        )}
        <div className={'bc-tx__icon ' + (t.kind === 'in' ? 'is-in' : 'is-out')}>
          {CAT_ABBR[t.category] ?? t.category.slice(0, 2).toUpperCase()}
        </div>
        <div className="bc-tx__desc">
          <span className="bc-tx__title">{t.title}</span>
          <span className="bc-tx__meta">
            {t.category && <span className="bc-badge bc-badge--cat">{t.category}</span>}
            {subName(t.subcategory_id) && <span className="bc-badge bc-badge--sub">{subName(t.subcategory_id)}</span>}
            {isChild && <span className="bc-badge bc-badge--amber" style={{ background: 'var(--amber-100)', color: 'var(--amber-800)' }}>Antecipada</span>}
            {t.note && <span className="bc-tx__note">{t.note}</span>}
          </span>
        </div>
        <span className="bc-tx__date">{t.date}</span>
        <span className={'bc-tx__amt ' + (t.kind === 'in' ? 'is-in' : 'is-out')}>
          {t.kind === 'in' ? '+' : '−'} R$ {fmtAmt(t.amount)}
        </span>
        <div className="bc-tx__actions">
          {onAmortize && t.installment_group_id && (t.installment_index ?? 0) < (t.installment_count ?? 0) && (
            <button
              className="bc-tx__amortize"
              aria-label="Adiantar parcelas"
              onClick={() => onAmortize(t)}
            >
              <FastForward size={14} />
            </button>
          )}
          {onEdit && (
            <button
              className="bc-tx__edit"
              aria-label="Editar lançamento"
              onClick={() => onEdit(t)}
            >
              <Pencil size={14} />
            </button>
          )}
          {onDelete && (
            <button
              className="bc-tx__del"
              aria-label="Excluir lançamento"
              onClick={() => (t.installment_group_id && onDeleteGroup) ? onDeleteGroup(t) : onDelete(t.id)}
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>
    )
  }

  const listItems = useMemo(() => groupedItems ? null : groupInstallments(filteredItems), [groupedItems, filteredItems])

  return (
    <div className="bc-card bc-list">
      <div className="bc-list__header">
        <h3 className="bc-list__title">{title}</h3>
        <div className="bc-list__header-actions">
          <div className="bc-list__search-wrap">
            <Search size={15} className="bc-list__search-icon" />
            <input
              className="bc-list__search"
              type="text"
              placeholder="Buscar por descrição ou valor..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Buscar transações por descrição ou valor"
            />
            {query && (
              <button
                className="bc-list__search-clear"
                type="button"
                onClick={() => setQuery('')}
                aria-label="Limpar busca"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <button className="bc-btn bc-btn--ghost bc-btn--sm" onClick={onAdd}>
            <Plus size={14} /><span>Adicionar</span>
          </button>
        </div>
      </div>
      <div className="bc-list__body">
        {loading && SKELETONS.map(n => (
          <div key={n} className="bc-tx">
            <div className="bc-tx__icon bc-tx__skel" style={{ borderRadius: 8 }} />
            <div className="bc-tx__desc">
              <span className="bc-tx__skel" style={{ width: '60%', height: 14, borderRadius: 4 }} />
              <span className="bc-tx__skel" style={{ width: '40%', height: 11, borderRadius: 4, marginTop: 2 }} />
            </div>
            <span className="bc-tx__skel" style={{ width: 28, height: 12, borderRadius: 4 }} />
            <span className="bc-tx__skel" style={{ width: 64, height: 14, borderRadius: 4 }} />
          </div>
        ))}

        {!loading && filteredItems.length === 0 && (
          <p className="bc-list__empty">
            {query ? 'Nenhuma transação encontrada para esta busca.' : 'Nenhum lançamento neste mês.'}
          </p>
        )}

        {!loading && listItems?.map(item => {
          if (item.type === 'transaction') return renderTransaction(item.transaction)

          const expanded = expandedGroups.has(item.groupId)
          return (
            <div key={`group-${item.groupId}`}>
              <div
                className="bc-tx"
                style={{ cursor: 'pointer', background: expanded ? 'var(--sand-1)' : undefined }}
                onClick={() => toggleGroup(item.groupId)}
              >
                <div className="bc-tx__icon is-out">
                  {CAT_ABBR[item.category] ?? item.category.slice(0, 2).toUpperCase()}
                </div>
                <div className="bc-tx__desc">
                  <span className="bc-tx__title">{item.title}</span>
                  <span className="bc-tx__meta">
                    <span className="bc-badge bc-badge--cat">{item.category}</span>
                    <span className="bc-badge bc-badge--amber" style={{ background: 'var(--amber-100)', color: 'var(--amber-800)' }}>{item.count} parcelas antecipadas</span>
                  </span>
                </div>
                <span className="bc-tx__date">{item.date}</span>
                <span className="bc-tx__amt is-out">− R$ {fmtAmt(item.total)}</span>
                <div className="bc-tx__actions" style={{ justifyContent: 'flex-end', opacity: 1 }}>
                  <button className="bc-btn bc-btn--ghost bc-btn--sm" style={{ padding: 4, height: 'auto' }}>
                    {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                </div>
              </div>
              {expanded && item.transactions.map(t => renderTransaction(t, true))}
            </div>
          )
        })}

        {!loading && groupedItems?.map(item => {
          if (item.type === 'transaction') return renderTransaction(item.transaction)

          return (
            <button
              key={`card-${item.card.id}`}
              className="bc-tx bc-card-group"
              onClick={() => onViewInvoice?.(item.card)}
            >
              <span />
              <div className="bc-tx__icon bc-card-group__icon">
                <CreditCard size={17} />
              </div>
              <div className="bc-tx__desc">
                <span className="bc-tx__title">{item.card.name}</span>
                <span className="bc-tx__meta">
                  <span className="bc-badge bc-badge--cat">
                    {item.count} {item.count === 1 ? 'lançamento' : 'lançamentos'}
                  </span>
                  <span className="bc-tx__note">Última data {toDisplayDate(item.lastFinancialDate)}</span>
                </span>
              </div>
              <span className="bc-tx__date">{toDisplayDate(item.lastFinancialDate)}</span>
              <span className="bc-tx__amt is-out">− R$ {fmtAmt(item.total)}</span>
              <span className="bc-card-group__open">Ver fatura</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
